import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import { toAppError } from "./errors";

/**
 * The only way to reach the backend.
 *
 * One wrapper so that error normalisation happens in a single place. Without
 * it, every call site handles Firebase's error shape, and the one that forgets
 * shows a teacher `functions/failed-precondition`.
 */
export async function call<TRequest, TResponse>(
  name: string,
  data?: TRequest
): Promise<TResponse> {
  try {
    const result = await httpsCallable<TRequest, TResponse>(functions(), name)(data);
    return result.data;
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Typed names, so a typo is a compile error rather than a 404 at run time.
 *
 * The list mirrors `functions/src/index.ts`. Adding a callable there and
 * forgetting it here means it cannot be called, which is the failure direction
 * to prefer.
 */
export const CALLABLES = {
  // identity
  me: "me",
  resolveUsername: "resolveUsername",
  completePasswordChange: "completePasswordChange",
  updateOwnProfile: "updateOwnProfile",

  // scene pictures, for the lesson review
  getSceneImageUrls: "getSceneImageUrls",

  // institution and people
  updateInstitution: "updateInstitution",
  createTeacher: "createTeacher",
  createPlayer: "createPlayer",
  createStudents: "createStudents",
  resetUserPassword: "resetUserPassword",
  setUserDisabled: "setUserDisabled",
  changeUserRole: "changeUserRole",
  deleteUser: "deleteUser",
  exportUser: "exportUser",

  // groups
  createGroup: "createGroup",
  updateGroup: "updateGroup",
  archiveGroup: "archiveGroup",
  listGroups: "listGroups",
  addToRoster: "addToRoster",
  removeFromRoster: "removeFromRoster",
  addGroupTeacher: "addGroupTeacher",
  removeGroupTeacher: "removeGroupTeacher",

  // import
  parseImportFile: "parseImportFile",
  mapImportColumns: "mapImportColumns",
  previewImportRows: "previewImportRows",
  commitImport: "commitImport",
  renderCredentialSheets: "renderCredentialSheets",

  // enrolment
  enrolDevice: "enrolDevice",
  listDevices: "listDevices",
  revokeDevice: "revokeDevice",

  // kits
  createKit: "createKit",
  listKits: "listKits",
  renameKit: "renameKit",
  rotateKitPassword: "rotateKitPassword",
  setKitNetwork: "setKitNetwork",

  // content
  createScenarioDraft: "createScenarioDraft",
  updateScenarioDraft: "updateScenarioDraft",
  validateScenarioDraft: "validateScenarioDraft",
  publishScenario: "publishScenario",
  shareScenario: "shareScenario",
  unshareScenario: "unshareScenario",
  duplicateScenario: "duplicateScenario",
  archiveScenario: "archiveScenario",
  listScenarioLibrary: "listScenarioLibrary",
  getScenarioVersion: "getScenarioVersion",
  getContentPack: "getContentPack",
  generateScenarioDraft: "generateScenarioDraft",

  // sessions
  createSession: "createSession",
  sessionCommand: "sessionCommand",
  endSession: "endSession",
  getSession: "getSession",
  listSessions: "listSessions",
  sessionReadiness: "sessionReadiness",
  getSessionReview: "getSessionReview",
  getGroupDries: "getGroupDries",
  setSessionScore: "setSessionScore",
  clearSessionScore: "clearSessionScore",
  saveSessionNotes: "saveSessionNotes",
  recordParentalConsent: "recordParentalConsent",
  revokeParentalConsent: "revokeParentalConsent",

  // legal
  getLegalStatus: "getLegalStatus",
  getLegalDocumentBody: "getLegalDocumentBody",
  resolveDocumentSet: "resolveDocumentSet",
  acceptLegalDocument: "acceptLegalDocument",
  decideSupportAccess: "decideSupportAccess",
  revokeSupportAccess: "revokeSupportAccess",

  // reports
  getInstitutionReport: "getInstitutionReport",
  getContentReport: "getContentReport",
  getActivityReport: "getActivityReport",
  getTeacherActivity: "getTeacherActivity",
  summariseStatistics: "summariseStatistics",

  // billing
  getBillingStatus: "getBillingStatus",
  createCheckoutSession: "createCheckoutSession",
  createPortalSession: "createPortalSession",
  setVatId: "setVatId",
  clearVatId: "clearVatId",
  getAiUsage: "getAiUsage",
} as const;

export type CallableName = (typeof CALLABLES)[keyof typeof CALLABLES];
